import React, { useState } from 'react';
import {
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  Tooltip,
  Checkbox,
  TableRow,
  TableCell,
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import MovieIcon from '@mui/icons-material/Movie';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import DescriptionIcon from '@mui/icons-material/Description';
import CodeIcon from '@mui/icons-material/Code';
import ArchiveIcon from '@mui/icons-material/Archive';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import { File } from '../../types';
import { canPreviewInBrowser } from '../../utils/previewUtils';

export interface FileListItemProps {
  file: File;
  onClick?: () => void;
  onOperation: (operation: string, fileId: string, data?: any) => void;
  isSelected: boolean;
  onSelectionChange: (selected: boolean) => void;
  listVariant?: 'default' | 'trash';
  layout?: 'list' | 'table';
  isSelectionMode?: boolean;
}

const getFileIcon = (mimetype: string | undefined) => {
  if (!mimetype) return <InsertDriveFileIcon color="disabled" />;

  if (mimetype.startsWith('image/')) return <ImageIcon />;
  if (mimetype === 'application/pdf') return <PictureAsPdfIcon color="error" />;
  if (mimetype.startsWith('video/')) return <MovieIcon color="secondary" />;
  if (mimetype.startsWith('audio/')) return <AudioFileIcon color="success" />;
  if (mimetype.startsWith('text/')) return <DescriptionIcon color="info" />;
  if (mimetype.includes('javascript') || mimetype.includes('json') || mimetype.includes('html') || mimetype.includes('css'))
    return <CodeIcon color="warning" />;
  if (mimetype.includes('zip') || mimetype.includes('compressed') || mimetype.includes('archive'))
    return <ArchiveIcon color="action" />;
  return <InsertDriveFileIcon color="disabled" />;
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const getFileType = (mimeType: string | undefined): string => {
  if (!mimeType) return 'Unknown';

  const type = mimeType.split('/')[0];
  switch (type) {
    case 'image':
      return 'Image';
    case 'video':
      return 'Video';
    case 'audio':
      return 'Audio';
    case 'application':
      if (mimeType === 'application/pdf') return 'PDF';
      if (mimeType.includes('word')) return 'Word Document';
      if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'Spreadsheet';
      if (mimeType.includes('presentation')) return 'Presentation';
      if (mimeType.includes('zip') || mimeType.includes('compressed')) return 'Archive';
      if (mimeType.includes('javascript') || mimeType.includes('json')) return 'Code';
      return 'Document';
    case 'text':
      if (mimeType.includes('javascript') || mimeType.includes('json')) return 'Code';
      return 'Text';
    default:
      return type.charAt(0).toUpperCase() + type.slice(1);
  }
};

const FileListItem: React.FC<FileListItemProps> = ({
  file,
  onClick,
  onOperation,
  isSelected,
  onSelectionChange,
  listVariant = 'default',
  layout = 'table',
  isSelectionMode = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const isTrash = listVariant === 'trash';

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.blur();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action: () => void) => {
    handleClose();
    action();
  };

  const handleItemClick = () => {
    onClick?.();
  };

  const handleStarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOperation('star', file.id, !file.isStarred);
  };

  const handleCheckboxClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onSelectionChange(!isSelected);
  };

  const isImage = file.mimeType?.startsWith('image/') || false;
  const canPreview = canPreviewInBrowser({
    name: file.name,
    mimeType: file.mimeType,
    mimetype: file.mimeType,
  });

  const fileIcon = (
    <Avatar
      variant="rounded"
      sx={{
        width: 36,
        height: 36,
        bgcolor: isImage ? 'primary.light' : 'background.paper',
        color: 'primary.main',
      }}
    >
      {getFileIcon(file.mimeType)}
    </Avatar>
  );

  const contextMenu = (
    <Menu
      id="file-list-menu"
      MenuListProps={{
        'aria-labelledby': `file-list-menu-${file.id}`,
      }}
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
    >
      {isTrash ? (
        <>
          <MenuItem onClick={() => handleAction(() => onOperation('preview', file.id))}>Open</MenuItem>
          <MenuItem onClick={() => handleAction(() => onOperation('restore', file.id))}>
            <RestoreFromTrashIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
            Restore
          </MenuItem>
          <MenuItem
            onClick={() => handleAction(() => onOperation('delete', file.id))}
            sx={{ color: 'error.main' }}
          >
            Delete permanently
          </MenuItem>
        </>
      ) : (
        <>
          {canPreview && (
            <MenuItem onClick={() => handleAction(() => onOperation('preview', file.id))}>Open</MenuItem>
          )}
          <MenuItem onClick={() => handleAction(() => onOperation('download', file.id))}>Download</MenuItem>
          <MenuItem onClick={() => handleAction(() => onOperation('share', file.id))}>Share</MenuItem>
          <MenuItem onClick={() => handleAction(() => onOperation('rename', file.id))}>Rename</MenuItem>
          <MenuItem onClick={() => handleAction(() => onOperation('copy', file.id))}>Make a copy</MenuItem>
          <MenuItem onClick={() => handleAction(() => onOperation('move', file.id))}>Move</MenuItem>
          <MenuItem onClick={() => handleAction(() => onOperation('star', file.id, !file.isStarred))}>
            {file.isStarred ? 'Remove from favorites' : 'Add to favorites'}
          </MenuItem>
          <MenuItem onClick={() => handleAction(() => onOperation('properties', file.id))}>
            Properties
          </MenuItem>
          <MenuItem
            onClick={() => handleAction(() => onOperation('delete', file.id))}
            sx={{ color: 'error.main' }}
          >
            Delete
          </MenuItem>
        </>
      )}
    </Menu>
  );

  if (layout === 'table') {
    return (
      <>
        <TableRow
          hover
          selected={isSelected}
          sx={{ cursor: 'pointer' }}
          onClick={handleItemClick}
          onContextMenu={handleClick}
        >
          <TableCell padding="checkbox">
            {isSelectionMode && (
              <Checkbox checked={isSelected} onClick={handleCheckboxClick} />
            )}
          </TableCell>
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
              {fileIcon}
              <Tooltip title={file.name} placement="top">
                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                  {file.name}
                </Typography>
              </Tooltip>
            </Box>
          </TableCell>
          <TableCell>
            <Typography variant="body2" color="text.secondary">
              {getFileType(file.mimeType)}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2" color="text.secondary">
              {formatFileSize(file.size)}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2" color="text.secondary" noWrap>
              {formatDate(file.updatedAt)}
            </Typography>
          </TableCell>
          <TableCell align="right" onClick={(e) => e.stopPropagation()}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              {!isTrash && (
                <IconButton
                  size="small"
                  aria-label="star"
                  onClick={handleStarClick}
                  sx={{ color: file.isStarred ? 'warning.main' : 'action.disabled' }}
                >
                  {file.isStarred ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                </IconButton>
              )}
              <IconButton
                size="small"
                aria-label="more"
                id={`file-list-menu-${file.id}`}
                aria-controls={open ? 'file-list-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup="true"
                onClick={handleClick}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
          </TableCell>
        </TableRow>
        {contextMenu}
      </>
    );
  }

  return (
    <ListItem
      alignItems="flex-start"
      sx={{
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
      onClick={handleItemClick}
      onContextMenu={handleClick}
      secondaryAction={
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Checkbox
            edge="end"
            checked={isSelected}
            onClick={handleCheckboxClick}
            sx={{ mr: 1 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            {formatFileSize(file.size)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2, minWidth: 120 }}>
            {formatDate(file.updatedAt)}
          </Typography>
          {!isTrash && (
            <IconButton
              edge="end"
              aria-label="star"
              onClick={handleStarClick}
              sx={{ color: file.isStarred ? 'warning.main' : 'action.disabled', mr: 1 }}
            >
              {file.isStarred ? <StarIcon /> : <StarBorderIcon />}
            </IconButton>
          )}
          <IconButton
            edge="end"
            aria-label="more"
            id={`file-list-menu-${file.id}`}
            aria-controls={open ? 'file-list-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="true"
            onClick={handleClick}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
      }
    >
      <ListItemAvatar>{fileIcon}</ListItemAvatar>
      <ListItemText
        primary={
          <Tooltip title={file.name} placement="top">
            <Typography variant="body1" noWrap sx={{ maxWidth: '300px', fontWeight: 'medium' }}>
              {file.name}
            </Typography>
          </Tooltip>
        }
        secondary={<Typography variant="body2" color="text.secondary">{getFileType(file.mimeType)}</Typography>}
      />
      {contextMenu}
    </ListItem>
  );
};

export default FileListItem;
